import { SuggestionAttribute } from './searchSuggestions';

// What people type for each filter value, so "a level past papers edexcel" becomes the
// A Level (GCE), Past Papers and Edexcel filters instead of a fuzzy text search where
// "a level" also matches International A Level, AS Level and Further.
//
// Keys must match the Typesense facet values exactly. Aliases are lowercase, use single
// spaces, and are matched as whole words, longest first, so "a level further" wins over
// "a level". The filler words below are stripped before matching, so aliases leave them out.
//
// Only add a word when the documents that use it in their name share that one filter value.
// "mock", "cheat sheet", "topic test" and "textbook" are left out because documents named
// with them are spread across several resource types, and a bare "further" is left out
// because it splits evenly between A Level Further and AS Level Further.
// Modules are left out: names like "p1" or "pure" are shared across courses and boards.
export const QUERY_FILTERS: Partial<Record<SuggestionAttribute, Record<string, string[]>>> = {
  doc_exam_course: {
    'A Level (GCE)': ['a level', 'a levels', 'alevel', 'alevels', 'gce a level'],
    'AS Level (GCE)': ['as level', 'as levels', 'aslevel'],
    'A Level Further (GCE)': ['a level further', 'further a level', 'a levels further'],
    'AS Level Further (GCE)': ['as level further', 'further as level', 'as further'],
    'International A Level': [
      'international a level',
      'international a levels',
      'international as level',
      'ial',
    ],
    GCSE: ['gcse', 'gcses'],
    'GCSE Further': ['gcse further', 'further gcse'],
    'GCSE Additional': ['gcse additional', 'additional gcse'],
    IGCSE: ['igcse', 'igcses', 'international gcse'],
    'IGCSE Further': ['igcse further', 'further igcse'],
    'IGCSE Additional': ['igcse additional', 'additional igcse'],
    'O Level': ['o level', 'o levels'],
    'O Level Additional': ['o level additional', 'additional o level'],
    '11+': ['11+', '11 plus', 'eleven plus'],
    'AP Calculus AB': ['ap calculus ab', 'ap calc ab', 'calculus ab'],
    'AP Calculus BC': ['ap calculus bc', 'ap calc bc', 'calculus bc'],
    'AP Statistics': ['ap statistics', 'ap stats'],
    IB: ['ib'],
    UKMT: ['ukmt'],
    STEP: ['step'],
    MAT: ['mat'],
    TMUA: ['tmua'],
    University: ['university'],
    'Primary Maths Challenge': ['primary challenge', 'pmc'],
  },
  doc_exam_board: {
    Edexcel: ['edexcel', 'pearson', 'pearson edexcel'],
    Cambridge: ['cambridge', 'cie', 'caie', 'cambridge international'],
    AQA: ['aqa'],
    OCR: ['ocr'],
    'OCR MEI': ['ocr mei', 'mei'],
  },
  doc_type: {
    'Past Papers': ['past papers', 'past paper', 'pastpapers'],
    'Practice Papers': ['practice papers', 'practice paper'],
    'Solution Bank': ['solution bank', 'solutions bank', 'solution banks'],
    'Predicted Papers': ['predicted papers', 'predicted paper'],
    'Worksheets By Topic': ['worksheets', 'worksheet', 'topic worksheets'],
    'Grade Boundaries': ['grade boundaries', 'grade boundary'],
    'Formulae Sheet': ['formulae sheet', 'formula sheet', 'formula booklet'],
    'Teaching Slides': ['teaching slides'],
  },
};

// Aliases that are also everyday words, so they only count when the query has another filter
// word: "edexcel as past papers" means AS Level, but "integration as a limit" is a document name.
// Checked after QUERY_FILTERS, so "writing as a fraction gcse" keeps GCSE and leaves "as" as text.
export const CONTEXT_QUERY_FILTERS: Partial<
  Record<SuggestionAttribute, Record<string, string[]>>
> = {
  doc_exam_course: {
    'AS Level (GCE)': ['as'],
  },
};

// Dropped from the query once a filter is found: on their own they match too little in
// document names, so "a level maths past papers" would otherwise filter down to nothing.
export const FILLER_WORDS = ['maths', 'math', 'mathematics', 'exam', 'exams', 'for', 'the'];
